import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/stores/authStore";

export function VerificationSuccess() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { toast } = useToast();
  const refreshUser = useAuthStore((state) => state.refreshUser);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Refresh user state to get updated verification status
    const updateState = async () => {
      const token = await getToken();
      if (token) {
        await refreshUser(token);
      }
    };
    updateState();

    // Focus heading for screen reader announcement
    headingRef.current?.focus();
  }, [getToken, refreshUser]);

  const handleContinue = () => {
    toast({
      title: "Welcome!",
      description: "Let's set up your profile.",
    });
    navigate("/profile/photos");
  };

  return (
    <main
      className="min-h-screen bg-gradient-to-b from-green-50 to-background flex items-center justify-center p-4"
      role="main"
      aria-labelledby="success-heading"
    >
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            aria-hidden="true"
          >
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h1
              id="success-heading"
              ref={headingRef}
              className="text-3xl font-bold text-bumble-dark mt-6"
              tabIndex={-1}
              aria-live="polite"
            >
              You're Verified!
            </h1>
            <p className="text-muted-foreground mt-2">
              Your teacher account is now verified. Privacy features unlocked.
            </p>

            <Button
              onClick={handleContinue}
              className="mt-8 w-full bg-green-500 hover:bg-green-600 text-white"
            >
              Continue
            </Button>
          </motion.div>
        </CardContent>
      </Card>
    </main>
  );
}
